import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ShieldCheck } from 'lucide-react'
import { LoginAnimations } from '../components/LoginAnimations/LoginAnimations'
import { DbLoginForm } from '../components/DbLoginForm'
import { ThemeToggle } from '../components/ThemeToggle/ThemeToggle'
import { BrandingLogo } from '../components/BrandingLogo'
import { getAppName } from '../config/appName'
import styles from './AdminLoginPage.module.css'

const hasSessionCookie = () =>
  document.cookie.split(';').some((c) => c.trim().startsWith('neuroiq_logged_in=1'))

export default function AdminLoginPage() {
  const [searchParams] = useSearchParams()
  const redirectUri = searchParams.get('redirect') || 'http://localhost:5173/dashboard'
  const info = searchParams.get('info')
  const appName = getAppName()
  const alreadyLoggedIn = !info && hasSessionCookie()

  // Redirect synchronously — return null to prevent any flash of the login page.
  useEffect(() => {
    if (alreadyLoggedIn) window.location.replace(redirectUri)
  }, [])

  if (alreadyLoggedIn) return null

  return (
    <div className={styles.page}>
      <LoginAnimations isVisible={true} />

      {info === 'session_expired' && (
        <div className={styles.infoBanner} role="alert">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Your session expired or you were signed out in another tab.
        </div>
      )}

      <div className={styles.logoContainer}>
        <BrandingLogo alt={appName} className={styles.logo} />
      </div>

      <div className={styles.themeToggleContainer}>
        <ThemeToggle />
      </div>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <div className={styles.badge}>
          <ShieldCheck className={styles.badgeIcon} />
          Administrator Access
        </div>

        <div className={styles.heading}>
          <h2 className={styles.title}>Admin Sign In</h2>
          <p className={styles.subtitle}>
            Enter your administrator credentials to continue.
          </p>
        </div>

        <DbLoginForm
          adminLogin
          usernameLabel="Admin Username"
          usernamePlaceholder="admin@neuroiq.com"
          redirectUri={redirectUri}
        />
      </motion.div>
    </div>
  )
}
